/* IntroExplainer — a true "flight-attendant style" walkthrough.
 *
 * Six animated React scenes; each scene's audio is a separate MP3 (English
 * then Arabic) recorded with OpenAI TTS (Nova). A soft music bed loops
 * underneath all six scenes at low volume.
 *
 * Visual scenes show actual UI fragments (training portal card, two-tab
 * layout, instruction icons, etc.) so the candidate sees what's being
 * narrated — like an airline safety briefing.
 */
import { useRef, useState } from "react";
import {
  Sparkles, FileText, Plane, ClipboardCheck, Compass,
  Stamp, MapPin, FileCheck, UploadCloud, Award,
} from "lucide-react";

const FULL_TRACK = "/explainer/full_mix.mp3";
// Cumulative start offsets (seconds) for each scene in `full_mix.mp3`.
const SCENE_OFFSETS = [0.0, 7.64, 26.82, 42.52, 59.56, 78.38];
const TRACK_TOTAL = 89.9;

const CAPTIONS = [
  { en: "Welcome to the Visa Operations Assessment.",
    ar: "أهلاً بك في تقييم دعم عمليات التأشيرات." },
  { en: "Your role: entering application data, reviewing passports, and booking appointments — accurately.",
    ar: "دورك: إدخال بيانات الطلبات، مراجعة الجوازات، وحجز المواعيد بدقّة." },
  { en: "First a short eligibility check, then a practical demo that mirrors real visa tasks.",
    ar: "اختبار أهلية قصير أولاً، ثم تجربة عملية تحاكي مهام التأشيرات الواقعية." },
  { en: "Read the case in the Training Portal, then complete the Visa Application Demo using the same data.",
    ar: "اقرأ المعاملة من بوابة التدريب، ثم عبّئ النموذج التجريبي بنفس البيانات." },
  { en: "Match the data, pick the correct biometric city, generate the PDF, and upload it back.",
    ar: "طابِق البيانات، اختر مدينة البصمة الصحيحة، أنشئ الملف، ثم ارفعه في البوابة." },
  { en: "Take your time, follow the steps, and best of luck.",
    ar: "خذ وقتك، اتبع الخطوات، ووفّقك الله." },
];

const c = { ink: "#f3eee2", muted: "#c9bfa6", accent: "#d4b56b", teal: "#5eead4",
            card: "rgba(255,255,255,0.06)", border: "rgba(255,255,255,0.14)" };

// ─────────────────── Scene visuals ───────────────────
function SceneWelcome() {
  return (
    <div style={center}>
      <div style={{ ...glowBadge, animation: "ex-pop 700ms ease-out both" }}>
        <Sparkles size={36} color={c.accent} strokeWidth={2} />
      </div>
      <h2 style={titleStyle}>Visa Operations Assessment</h2>
      <p style={subStyle} dir="rtl">تقييم دعم عمليات التأشيرات</p>
    </div>
  );
}

function SceneRole() {
  const items = [
    { I: FileText, en: "Application data", ar: "بيانات الطلبات" },
    { I: Stamp,    en: "Passport review",   ar: "مراجعة الجوازات" },
    { I: Plane,    en: "Appointment slots", ar: "حجز المواعيد" },
  ];
  return (
    <div style={center}>
      <h2 style={titleStyle}>What you&apos;ll do</h2>
      <div style={{ display: "flex", gap: 14, marginTop: 18, flexWrap: "wrap", justifyContent: "center" }}>
        {items.map((it, i) => (
          <div key={i} style={{
            ...miniCard, animation: `ex-rise 600ms ${i * 180}ms ease-out both`,
          }}>
            <it.I size={26} color={c.accent} strokeWidth={2} />
            <div style={{ fontSize: 12.5, fontWeight: 800, marginTop: 7 }}>{it.en}</div>
            <div dir="rtl" style={{ fontSize: 11, color: c.muted, marginTop: 2 }}>{it.ar}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SceneHow() {
  return (
    <div style={center}>
      <h2 style={titleStyle}>How the assessment works</h2>
      <div style={{ display: "flex", gap: 18, marginTop: 22, alignItems: "center" }}>
        <Step idx={1} I={ClipboardCheck} en="Eligibility check" ar="اختبار أهلية" delay={0}/>
        <Arrow delay={350}/>
        <Step idx={2} I={Compass} en="Practical demo" ar="تجربة عملية" delay={700}/>
      </div>
    </div>
  );
}

function Step({ idx, I, en, ar, delay }) {
  return (
    <div style={{
      ...miniCard, minWidth: 140, animation: `ex-rise 500ms ${delay}ms ease-out both`,
    }}>
      <div style={{ width: 32, height: 32, borderRadius: 16, background: `${c.accent}22`,
                    color: c.accent, display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 900, fontSize: 14, marginBottom: 6 }}>{idx}</div>
      <I size={22} color={c.accent} strokeWidth={2} />
      <div style={{ fontSize: 12.5, fontWeight: 800, marginTop: 6 }}>{en}</div>
      <div dir="rtl" style={{ fontSize: 11, color: c.muted, marginTop: 2 }}>{ar}</div>
    </div>
  );
}

function Arrow({ delay }) {
  return <div style={{ animation: `ex-fade 500ms ${delay}ms ease-out both`,
                       color: c.accent, fontSize: 26, fontWeight: 900 }}>→</div>;
}

function SceneTabs() {
  return (
    <div style={center}>
      <h2 style={titleStyle}>Two tabs · تبويبان</h2>
      <div style={{
        display: "flex", gap: 0, marginTop: 18,
        border: `1px solid ${c.border}`, borderRadius: 12, overflow: "hidden",
        animation: "ex-rise 500ms ease-out both",
      }}>
        <div style={{ ...tab, background: `${c.accent}28`, borderInlineEnd: `1px solid ${c.border}` }}>
          <FileText size={18} color={c.accent}/>
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 800 }}>Training Portal</div>
            <div dir="rtl" style={{ fontSize: 10.5, color: c.muted, marginTop: 2 }}>بوابة التدريب</div>
          </div>
        </div>
        <div style={{ ...tab, background: "rgba(94,234,212,0.12)" }}>
          <ClipboardCheck size={18} color={c.teal}/>
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 800 }}>Visa Application Demo</div>
            <div dir="rtl" style={{ fontSize: 10.5, color: c.muted, marginTop: 2 }}>النموذج التجريبي</div>
          </div>
        </div>
      </div>
      <p style={{ ...subStyle, marginTop: 14, maxWidth: 460 }}>
        Read the case here → then enter it there.
      </p>
      <p dir="rtl" style={{ ...subStyle, marginTop: 4, maxWidth: 460, fontSize: 13 }}>
        اقرأ من اليمين → أدخل في اليسار.
      </p>
    </div>
  );
}

function SceneTips() {
  const tips = [
    { I: FileCheck,   en: "Match the data",     ar: "طابِق البيانات" },
    { I: MapPin,      en: "Biometric city",     ar: "مدينة البصمة" },
    { I: FileText,    en: "Generate PDF",        ar: "أنشئ ملف PDF" },
    { I: UploadCloud, en: "Upload back",         ar: "ارفع في البوابة" },
  ];
  return (
    <div style={center}>
      <h2 style={titleStyle}>Accuracy matters</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 10, marginTop: 16, maxWidth: 460 }}>
        {tips.map((t, i) => (
          <div key={i} style={{
            ...tipRow, animation: `ex-rise 500ms ${i * 150}ms ease-out both`,
          }}>
            <t.I size={18} color={c.accent}/>
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 800 }}>{t.en}</div>
              <div dir="rtl" style={{ fontSize: 11, color: c.muted, marginTop: 1 }}>{t.ar}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SceneClosing() {
  return (
    <div style={center}>
      <div style={{ ...glowBadge, animation: "ex-pop 700ms ease-out both" }}>
        <Award size={32} color={c.accent} strokeWidth={2.2}/>
      </div>
      <h2 style={titleStyle}>Ready to begin?</h2>
      <p style={subStyle}>Take your time, follow the steps, and best of luck.</p>
      <p dir="rtl" style={{ ...subStyle, fontSize: 14, color: c.accent, marginTop: 6 }}>
        خذ وقتك، اتبع الخطوات، ووفّقك الله.
      </p>
    </div>
  );
}

const SCENE_RENDERERS = [SceneWelcome, SceneRole, SceneHow, SceneTabs, SceneTips, SceneClosing];

// ────────────────── Player ──────────────────
export default function IntroExplainer() {
  const voiceRef = useRef(null);
  const tickRef = useRef(0);
  const finishedRef = useRef(false);
  const [started, setStarted] = useState(false);
  const [muted, setMuted] = useState(true);
  const [scene, setScene] = useState(0);
  const [progress, setProgress] = useState(0);

  // Walk through scenes based on currentTime. Throttled to 8fps so React
  // doesn't re-render on every audio frame (which was causing the player to
  // appear to "stall" in preview environments).
  const onTimeUpdate = () => {
    const v = voiceRef.current; if (!v) return;
    tickRef.current = (tickRef.current + 1) % 6;
    if (tickRef.current !== 0) return;
    const t = v.currentTime;
    let idx = 0;
    for (let i = SCENE_OFFSETS.length - 1; i >= 0; i--) {
      if (t >= SCENE_OFFSETS[i]) { idx = i; break; }
    }
    setScene((cur) => (cur === idx ? cur : idx));
    setProgress((t / TRACK_TOTAL) * 100);
  };

  // The browser sometimes pauses long-running audio (tab heuristics, network
  // hiccups). Re-issue play() automatically until the track ends.
  const onPause = () => {
    const v = voiceRef.current;
    if (!v || finishedRef.current) return;
    if (v.ended) { finishedRef.current = true; return; }
    // Brief delay so we don't fight an explicit user pause.
    setTimeout(() => {
      const vv = voiceRef.current;
      if (!vv || vv.ended || finishedRef.current) return;
      vv.play().catch(() => {});
    }, 120);
  };

  const onEnded = () => {
    finishedRef.current = true;
  };

  const start = async () => {
    finishedRef.current = false;
    const v = voiceRef.current;
    if (v) {
      v.currentTime = 0;
      v.muted = false;
      v.volume = 1;
      try { await v.play(); } catch (_) { /* ignore */ }
    }
    setMuted(false);
    setStarted(true);
    setScene(0);
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    if (voiceRef.current) voiceRef.current.muted = next;
  };

  const restart = () => {
    finishedRef.current = false;
    const v = voiceRef.current;
    if (v) { v.currentTime = 0; v.play().catch(() => {}); }
    setScene(0);
  };

  const cap = CAPTIONS[scene];
  const Scene = SCENE_RENDERERS[scene];
  const isLast = scene === SCENE_OFFSETS.length - 1;

  return (
    <div data-testid="intro-explainer" style={shell}>
      <audio ref={voiceRef} src={FULL_TRACK}
             onTimeUpdate={onTimeUpdate}
             onPause={onPause}
             onEnded={onEnded}
             preload="auto" />

      {/* Stage */}
      <div key={`scene-${scene}`} style={stage}>
        <Scene />
      </div>

      {/* Caption strip */}
      {started && (
        <div style={captionBox} data-testid={`scene-caption-${scene}`}>
          <div style={{ fontSize: "clamp(11.5px, 2.9vw, 13.5px)", fontWeight: 800, lineHeight: 1.45 }}>{cap.en}</div>
          <div dir="rtl" style={{ fontSize: "clamp(11px, 2.7vw, 12.5px)", color: c.accent, marginTop: 3, lineHeight: 1.6 }}>{cap.ar}</div>
        </div>
      )}

      {/* Progress bar */}
      {started && (
        <div style={progressTrack}>
          <div style={{ ...progressFill, width: `${progress}%` }} />
        </div>
      )}

      {/* Initial tap-to-start overlay */}
      {!started && (
        <button onClick={start} data-testid="intro-unmute" style={overlay}>
          <div style={playCircle}>▶</div>
          <div style={{ fontSize: 14.5, fontWeight: 800, marginTop: 10, color: "#fff" }}>
            Tap to play with sound
          </div>
          <div dir="rtl" style={{ fontSize: 12.5, marginTop: 4, color: c.muted }}>
            اضغط لتشغيل الفيديو بالصوت
          </div>
        </button>
      )}

      {/* Small controls after start */}
      {started && (
        <div style={{ position: "absolute", top: 10, insetInlineEnd: 10, display: "flex", gap: 6 }}>
          {isLast && (
            <button onClick={restart} title="إعادة التشغيل" data-testid="intro-restart"
                    style={muteBtn}>↻</button>
          )}
          <button onClick={toggleMute} title={muted ? "تشغيل الصوت" : "كتم الصوت"}
                  data-testid="intro-mute-toggle"
                  style={muteBtn}>{muted ? "🔇" : "🔊"}</button>
        </div>
      )}

      <style>{`
        @keyframes ex-pop { 0% { opacity: 0; transform: scale(.7); } 100% { opacity: 1; transform: scale(1); } }
        @keyframes ex-rise { 0% { opacity: 0; transform: translateY(14px); } 100% { opacity: 1; transform: translateY(0); } }
        @keyframes ex-fade { 0% { opacity: 0; } 100% { opacity: 1; } }
      `}</style>
    </div>
  );
}

// ─────────────────── Styles ───────────────────
const shell = {
  position: "relative", borderRadius: 14, overflow: "hidden",
  marginBottom: 18, border: "1px solid #2a2a35",
  background: "linear-gradient(180deg, #14151b, #0b0c12)",
  width: "100%", minHeight: 320, height: "clamp(320px, 52vw, 420px)",
  color: c.ink,
  boxShadow: "0 6px 30px rgba(0,0,0,0.18)",
  fontFamily: "system-ui, -apple-system, 'Segoe UI', Tahoma, sans-serif",
};
const stage = {
  position: "absolute", top: 0, left: 0, right: 0,
  bottom: 130,   // reserve room for the caption block
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: "16px 16px 4px",
};
const center = { display: "flex", flexDirection: "column", alignItems: "center",
                 textAlign: "center", width: "100%", maxWidth: 540 };
const titleStyle = { margin: 0, fontSize: "clamp(16px, 4.2vw, 22px)",
                     fontWeight: 900, letterSpacing: 0.2, color: c.ink };
const subStyle = { margin: "6px 0 0", fontSize: "clamp(11.5px, 2.8vw, 13px)",
                   color: c.muted, lineHeight: 1.6 };
const glowBadge = {
  width: 52, height: 52, borderRadius: 14,
  background: `${c.accent}22`, color: c.accent,
  display: "flex", alignItems: "center", justifyContent: "center",
  boxShadow: `0 0 24px ${c.accent}40`, marginBottom: 10,
};
const miniCard = {
  background: c.card, border: `1px solid ${c.border}`,
  borderRadius: 10, padding: "9px 10px", minWidth: 92, maxWidth: 130,
  textAlign: "center", display: "flex", flexDirection: "column",
  alignItems: "center", flex: "0 1 110px",
};
const tab = { display: "flex", gap: 8, padding: "10px 12px",
              alignItems: "center", textAlign: "start", flex: 1 };
const tipRow = {
  display: "flex", gap: 8, alignItems: "center", textAlign: "start",
  background: c.card, border: `1px solid ${c.border}`,
  borderRadius: 10, padding: "8px 10px",
};
const captionBox = {
  position: "absolute", left: 12, right: 12, bottom: 16,
  textAlign: "center", color: c.ink,
  animation: "ex-fade 500ms ease-out both",
  textShadow: "0 1px 4px rgba(0,0,0,0.6)",
  padding: "8px 10px",
  background: "linear-gradient(180deg, rgba(11,12,18,0) 0%, rgba(11,12,18,0.55) 40%, rgba(11,12,18,0.85) 100%)",
  borderRadius: 10,
  maxHeight: 110, overflow: "hidden",
};
const progressTrack = {
  position: "absolute", left: 0, right: 0, bottom: 0, height: 3,
  background: "rgba(255,255,255,0.08)",
};
const progressFill = { height: "100%", background: c.accent, transition: "width 120ms linear" };
const overlay = {
  position: "absolute", inset: 0, display: "flex", flexDirection: "column",
  alignItems: "center", justifyContent: "center",
  padding: 24,
  background: "linear-gradient(180deg, rgba(10,10,15,0.45) 0%, rgba(10,10,15,0.80) 100%)",
  border: "none", cursor: "pointer", fontFamily: "inherit",
};
const playCircle = {
  width: 56, height: 56, borderRadius: "50%",
  background: c.accent, color: "#0a0a0f",
  display: "flex", alignItems: "center", justifyContent: "center",
  fontSize: 22, fontWeight: 900, boxShadow: "0 6px 24px rgba(0,0,0,0.5)",
};
const muteBtn = {
  position: "relative",
  background: "rgba(0,0,0,0.55)", color: "#fff",
  border: "1px solid rgba(255,255,255,0.25)",
  padding: "5px 9px", borderRadius: 16, fontSize: 12, cursor: "pointer",
  backdropFilter: "blur(8px)",
};
